import { useCallback, useEffect, useRef } from 'react';
import {
  MESSAGE_TYPES,
  VAD_STATES,
  VOICE_MODE_STATUS
} from '../constants/simulationConstants';
import { blobToBase64, postMessage } from '../helpers/utils';
import { isClipSendable, pickRecorderMimeType } from '../helpers/liveChatAudio';
import { useSimulationContext } from '../providers/SimulationProvider';
import { setupAudioWorklet } from '../lib/vadAudioProcessor';
import coreLiveChatConfig from '../config/coreLiveChatConfig';

// getUserMedia rejection names the host matches on verbatim.
const MIC_ERROR_MESSAGES = {
  NotAllowedError: 'microphone access denied',
  NotFoundError: 'microphone unavailable',
  NotReadableError: 'microphone unavailable'
};

const UNSUPPORTED_BROWSER_ERROR = 'Live Chat is not supported on this browser';

/**
 * Mic capture and turn segmentation for Nudgyt Core Live Chat.
 *
 * Core sends one finished clip per turn, so this owns the recorder as well as
 * the VAD — unlike {@link useVoiceActivityDetection}, where the Convai SDK
 * streams the mic itself. Reuses lib/vadAudioProcessor on its own AudioContext.
 *
 * Mount inside the Core client hook, not the component: barge-in needs the
 * client's audio queue and response timeout.
 *
 * @param sendAudioQuery async (base64Audio, format) => boolean; false when the
 *   socket is closed, which drops the clip.
 * @param onUserBargeIn stops in-flight AI playback; differs per renderer.
 * @param clearResponseTimeout cancels the pending reply timer.
 * @returns postLiveChatStatus/startListening, for timeout recovery.
 */
function useCoreLiveChat({
  sendAudioQuery,
  onUserBargeIn,
  clearResponseTimeout
} = {}) {
  const {
    isAiTalking,
    isLiveChatActive,
    isLiveChatInterrupt,
    isLocalAudioPlaying,
    setIsLiveChatActive,
    setIsLiveChatInterrupt
  } = useSimulationContext();

  const audioContextRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const processorRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const clipStartedAtRef = useRef(0);
  const silenceTimerRef = useRef(null);
  const maxClipTimerRef = useRef(null);
  const isSetupRef = useRef(false);
  const discardClipRef = useRef(false);
  const prevIsAiTalkingRef = useRef(false);

  // Both refs feed the worklet callback, which is created once at setup and
  // would otherwise read stale values on every frame.
  const isMicMutedRef = useRef(false);
  const propsRef = useRef(null);
  propsRef.current = { sendAudioQuery, onUserBargeIn, clearResponseTimeout };

  const postLiveChatStatus = useCallback((status) => {
    postMessage({ type: MESSAGE_TYPES.LIVE_CHAT, payload: { status } });
  }, []);

  const postLiveChatError = useCallback(
    (message) => {
      postMessage({
        type: MESSAGE_TYPES.LIVE_CHAT,
        error: message,
        payload: { status: VOICE_MODE_STATUS.INACTIVE }
      });
      setIsLiveChatActive(false);
    },
    [setIsLiveChatActive]
  );

  const startListening = useCallback((options = {}) => {
    if (!isSetupRef.current) return;

    isMicMutedRef.current = false;
    if (!options.onlyUnmute) {
      postLiveChatStatus(VAD_STATES.IDLE);
    }
  }, [postLiveChatStatus]);

  const clearTurnTimers = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
    if (maxClipTimerRef.current) {
      clearTimeout(maxClipTimerRef.current);
      maxClipTimerRef.current = null;
    }
  };

  // Muting here rather than on isAiTalking stops a trailing breath from
  // reopening the mic before the reply arrives.
  const closeTurn = () => {
    clearTurnTimers();
    if (recorderRef.current?.state !== 'recording') return;

    isMicMutedRef.current = true;
    postLiveChatStatus(VAD_STATES.AI_ANALYZING);
    recorderRef.current.stop();
  };

  const handleVoiceDetected = () => {
    if (isMicMutedRef.current) return;

    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }

    // MediaRecorder.state is authoritative, so no debounce guard is needed.
    const recorder = recorderRef.current;
    if (!recorder || recorder.state === 'recording') return;

    propsRef.current.onUserBargeIn?.();
    propsRef.current.clearResponseTimeout?.();

    chunksRef.current = [];
    clipStartedAtRef.current = Date.now();
    recorder.start();
    maxClipTimerRef.current = setTimeout(
      closeTurn,
      coreLiveChatConfig.maxClipMs
    );

    postLiveChatStatus(VAD_STATES.USER_ACTIVE);
  };

  const handleVoiceSilence = () => {
    if (recorderRef.current?.state !== 'recording') return;
    if (silenceTimerRef.current) return;

    silenceTimerRef.current = setTimeout(
      closeTurn,
      coreLiveChatConfig.silenceDurationMs
    );
  };

  const handleRecorderStop = async (recorder) => {
    const chunks = chunksRef.current;
    const durationMs = Date.now() - clipStartedAtRef.current;
    chunksRef.current = [];

    if (discardClipRef.current) {
      discardClipRef.current = false;
      return;
    }

    const clip = new Blob(chunks, { type: recorder.mimeType });
    const canSend = isClipSendable({
      size: clip.size,
      durationMs,
      minClipBytes: coreLiveChatConfig.minClipBytes,
      minClipMs: coreLiveChatConfig.minClipMs
    });

    // Nothing was asked, so no response timeout.
    if (!canSend) {
      startListening();
      return;
    }

    try {
      const base64Audio = await blobToBase64(clip);
      const isSent = await propsRef.current.sendAudioQuery?.(
        base64Audio,
        recorder.mimeType
      );

      if (!isSent) {
        startListening();
        return;
      }

    } catch (error) {
      console.error('[Core Live Chat Error] Failed to encode clip:', error);
      startListening();
    }
  };

  const setupLiveChat = async () => {
    if (isSetupRef.current) return;

    const mimeType = pickRecorderMimeType(
      coreLiveChatConfig.recorderMimeTypeCandidates
    );
    if (!mimeType) {
      postLiveChatError(UNSUPPORTED_BROWSER_ERROR);
      return;
    }

    // Claimed before the first await: a second setup would register
    // 'vad-processor' twice on one context and throw.
    isSetupRef.current = true;

    try {
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      audioContextRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();

      // The Live Chat click lands in the parent document, which is not a
      // gesture here, so a fresh context can start suspended.
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }

      const source = audioContextRef.current.createMediaStreamSource(
        mediaStreamRef.current
      );

      const workletSetup = await setupAudioWorklet(
        audioContextRef.current,
        source
      );

      processorRef.current = workletSetup.node;
      audioContextRef.current.vadCleanup = workletSetup.cleanup;

      workletSetup.node.port.onmessage = (event) => {
        if (!isSetupRef.current) return;

        const { confidence } = event.data;

        if (confidence > coreLiveChatConfig.voiceConfidenceThreshold) {
          handleVoiceDetected();
        } else {
          handleVoiceSilence();
        }
      };

      const recorder = new window.MediaRecorder(mediaStreamRef.current, {
        mimeType
      });
      recorder.ondataavailable = (event) => {
        if (event.data?.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = () => handleRecorderStop(recorder);
      recorderRef.current = recorder;

      startListening();
    } catch (error) {
      console.error(
        '[Core Live Chat Error] Failed to setup:',
        error?.name,
        error
      );
      isSetupRef.current = false;
      postLiveChatError(
        MIC_ERROR_MESSAGES[error.name] ?? UNSUPPORTED_BROWSER_ERROR
      );
    }
  };

  const cleanupLiveChat = () => {
    isSetupRef.current = false;
    clearTurnTimers();

    if (recorderRef.current?.state === 'recording') {
      discardClipRef.current = true;
      recorderRef.current.stop();
    }
    recorderRef.current = null;
    chunksRef.current = [];

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current.port.close?.();
      processorRef.current = null;
    }

    if (audioContextRef.current?.vadCleanup) {
      audioContextRef.current.vadCleanup();
      audioContextRef.current.vadCleanup = null;
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    isMicMutedRef.current = false;
    prevIsAiTalkingRef.current = false;
  };

  useEffect(() => {
    if (!isLiveChatActive) return undefined;

    setupLiveChat();
    return () => cleanupLiveChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLiveChatActive]);

  useEffect(() => {
    if (!isLiveChatActive || !isSetupRef.current) return;

    const wasAiTalking = prevIsAiTalkingRef.current;
    prevIsAiTalkingRef.current = isAiTalking;

    if (!wasAiTalking && isAiTalking) {
      postLiveChatStatus(VAD_STATES.AI_ACTIVE);
      return;
    }

    if (wasAiTalking && !isAiTalking) {
      if (isLocalAudioPlaying) return;

      startListening();
    }
  }, [
    isAiTalking,
    isLiveChatActive,
    isLocalAudioPlaying,
    postLiveChatStatus,
    startListening
  ]);

  useEffect(() => {
    if (!isLiveChatActive || !isLiveChatInterrupt) return;

    propsRef.current.onUserBargeIn?.();
    startListening();
    setIsLiveChatInterrupt(false);
  }, [
    isLiveChatActive,
    isLiveChatInterrupt,
    setIsLiveChatInterrupt,
    startListening
  ]);

  return { postLiveChatStatus, startListening };
}

export default useCoreLiveChat;
